/**
 * Filter component for Audit Logs
 * Comprehensive filtering with time range, status, method, source, and search
 */

import React, { useState } from "react";
import {
  Box,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Select,
  Input,
  Button,
  Divider,
  useColorModeValue,
  Flex,
  Badge,
} from "@chakra-ui/react";
import { FiSearch, FiX, FiFilter } from "react-icons/fi";
import { AuditLogFilters, TimeRangeOption } from "./types";
import { TIME_RANGE_LABELS, formatDateForAPI } from "./utils";

interface FilterAuditLogsProps {
  onFiltersChange: (filters: AuditLogFilters) => void;
  loading?: boolean;
}

const FilterAuditLogs: React.FC<FilterAuditLogsProps> = ({
  onFiltersChange,
  loading = false,
}) => {
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "gray.700");

  // Filter state
  const [timeRange, setTimeRange] = useState<TimeRangeOption>("last_24_hours");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [method, setMethod] = useState<string>("");
  const [source, setSource] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [deviceId, setDeviceId] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [activeFilters, setActiveFilters] = useState(0);

  const handleApplyFilters = () => {
    const filters: AuditLogFilters = {
      page: 1,
      per_page: 50,
      time_range: showCustomDate ? "custom" : timeRange,
    };

    if (showCustomDate && startDate && endDate) {
      filters.start_date = formatDateForAPI(startDate);
      filters.end_date = formatDateForAPI(endDate);
    }

    if (status !== "") {
      filters.success = status === "success";
    }

    if (method) filters.method = method as "ldap" | "jwt";
    if (source) filters.source = source as "mobile" | "web" | "api";
    if (email) filters.email = email;
    if (deviceId) filters.device_id = deviceId;
    if (searchText) filters.search = searchText;

    onFiltersChange(filters);
    updateActiveFiltersCount();
  };

  const updateActiveFiltersCount = () => {
    let count = 0;
    if (status !== "") count++;
    if (method) count++;
    if (source) count++;
    if (email) count++;
    if (deviceId) count++;
    if (searchText) count++;
    if (showCustomDate && startDate && endDate) count++;
    setActiveFilters(count);
  };

  const handleReset = () => {
    setTimeRange("last_24_hours");
    setStartDate("");
    setEndDate("");
    setStatus("");
    setMethod("");
    setSource("");
    setEmail("");
    setDeviceId("");
    setSearchText("");
    setShowCustomDate(false);
    setActiveFilters(0);
    onFiltersChange({
      page: 1,
      per_page: 50,
      time_range: "last_24_hours",
    });
  };

  const handleTimeRangeChange = (value: string) => {
    if (value === "custom") {
      setShowCustomDate(true);
      setTimeRange("custom");
    } else {
      setShowCustomDate(false);
      setTimeRange(value as TimeRangeOption);
    }
  };

  return (
    <Box
      bg={bg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="lg"
      p={4}
    >
      <VStack align="stretch" spacing={4}>
        {/* Header dengan badge filter count */}
        <HStack justify="space-between">
          <HStack spacing={2}>
            <FiFilter />
            <span style={{ fontWeight: "600" }}>Filter Audit Log</span>
            {activeFilters > 0 && (
              <Badge colorScheme="blue" variant="solid">
                {activeFilters} aktif
              </Badge>
            )}
          </HStack>
        </HStack>

        <Divider />

        {/* Time Range Selection */}
        <FormControl>
          <FormLabel fontSize="sm" fontWeight="600">
            Rentang Waktu
          </FormLabel>
          <Select
            value={timeRange}
            onChange={(e) => handleTimeRangeChange(e.target.value)}
            size="sm"
            isDisabled={loading}
          >
            {Object.entries(TIME_RANGE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </FormControl>

        {/* Custom Date Range */}
        {showCustomDate && (
          <HStack spacing={3} align="flex-end">
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600">
                Dari Tanggal
              </FormLabel>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                size="sm"
                isDisabled={loading}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600">
                Sampai Tanggal
              </FormLabel>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                size="sm"
                isDisabled={loading}
              />
            </FormControl>
          </HStack>
        )}

        <Divider />

        {/* Status Filter */}
        <FormControl>
          <FormLabel fontSize="sm" fontWeight="600">
            Status
          </FormLabel>
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            size="sm"
            placeholder="Semua Status"
            isDisabled={loading}
          >
            <option value="success">Berhasil</option>
            <option value="failed">Gagal</option>
          </Select>
        </FormControl>

        {/* Method Filter */}
        <FormControl>
          <FormLabel fontSize="sm" fontWeight="600">
            Metode Autentikasi
          </FormLabel>
          <Select
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            size="sm"
            placeholder="Semua Metode"
            isDisabled={loading}
          >
            <option value="ldap">LDAP</option>
            <option value="jwt">JWT</option>
          </Select>
        </FormControl>

        {/* Source Filter */}
        <FormControl>
          <FormLabel fontSize="sm" fontWeight="600">
            Sumber Login
          </FormLabel>
          <Select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            size="sm"
            placeholder="Semua Sumber"
            isDisabled={loading}
          >
            <option value="mobile">Mobile</option>
            <option value="web">Web</option>
            <option value="api">API</option>
          </Select>
        </FormControl>

        <Divider />

        {/* Email Search */}
        <FormControl>
          <FormLabel fontSize="sm" fontWeight="600">
            Email Pengguna
          </FormLabel>
          <Input
            placeholder="Cari berdasarkan email..."
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            size="sm"
            isDisabled={loading}
          />
        </FormControl>

        {/* Device ID Search */}
        <FormControl>
          <FormLabel fontSize="sm" fontWeight="600">
            Device ID
          </FormLabel>
          <Input
            placeholder="Cari berdasarkan device ID..."
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            size="sm"
            isDisabled={loading}
          />
        </FormControl>

        {/* Free Text Search */}
        <FormControl>
          <FormLabel fontSize="sm" fontWeight="600">
            Pencarian Bebas
          </FormLabel>
          <Input
            placeholder="Cari email, device ID, atau alasan kegagalan..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            size="sm"
            isDisabled={loading}
          />
        </FormControl>

        <Divider />

        {/* Action Buttons */}
        <Flex gap={2} justify="flex-end">
          <Button
            size="sm"
            variant="outline"
            leftIcon={<FiX />}
            onClick={handleReset}
            isDisabled={loading}
          >
            Reset
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            onClick={handleApplyFilters}
            isLoading={loading}
          >
            Terapkan Filter
          </Button>
        </Flex>
      </VStack>
    </Box>
  );
};

export default FilterAuditLogs;
